import { Col, Modal, Row } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import VSpace from '../../common/VSpace';
import { DatacenterReportTypes, DateRanges, getDatacenterReportTypeNames, getFiltersFromJson, ReportFilterViewModel, updateFilterDates } from '../datacenterModel';
import { useCreateFiltersMutation, useDeleteFiltersMutation, useGetFiltersQuery } from '../datacenterQueries';
import ReportFilters from '../ReportFilters';
import ReportSavedFiltersList from '../ReportSavedFiltersList';
import SaveFilterModal from '../SaveFilterModal';
import { useGetManHoursProjectsDataQuery } from './manHoursReportQueries';
import { ManHoursReportData } from './manHoursReportApi';
import ManHoursReportFilterDetails from './ManHoursReportFilterDetails';
import ManHoursReportList from './ManHoursReportList';
import { getActualManHoursStatistics, getEstimatedManHoursStatistics, getFilteredManHoursReport, getUniqueDivisionNames, getUniqueSalesPersonNames } from './manHoursReportModel';
import ReportStatistics from '../ReportStatistics';
import { projectTypeOptionsFor } from '../../../common/constants';
import useServiceTicketsEnabled from '../../../hooks/useServiceTicketsEnabled';

export type ManHoursReportFilters = {
  startDate: Dayjs;
  endDate: Dayjs;
  salesPersonIds: string[];
  projectTypeIds: number[];
  divisionIds: string[];
  dateRange: DateRanges;
}

const ManHoursReport = () => {
  const showServiceTickets = useServiceTicketsEnabled()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const today = dayjs();
  const defaultStartDate = today.subtract(6, 'month');
  const defaultFilters: ManHoursReportFilters = {
    startDate: defaultStartDate,
    endDate: today,
    salesPersonIds: [],
    projectTypeIds: [],
    divisionIds: [],
    dateRange: 'custom'
  }

  const [open, setOpen] = useState(false);
  const [deleteFilterModalConfirm, deleteFiltersModalContext] = Modal.useModal();
  const [filterName, setFilterName] = useState('');
  const [filters, setFilters] = useState<ManHoursReportFilters>(defaultFilters);
  const { startDate, endDate, salesPersonIds, divisionIds } = filters;

  const getManHoursProjectsDataQuery = useGetManHoursProjectsDataQuery(
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0]
  );
  const getFiltersQuery = useGetFiltersQuery(DatacenterReportTypes.ManHours)
  const createFiltersMutation = useCreateFiltersMutation()
  const deleteFiltersMutation = useDeleteFiltersMutation()

  const filtersList: ReportFilterViewModel[] = getFiltersQuery.data?.map((x) => {
    return {
      ...x,
      filters: getFiltersFromJson(x.filters)
    }
  }).sort((a, b) => a.order - b.order) || [];

  const jobTypesSet = new Set(filters.projectTypeIds)
  const filteredManHoursReport: ManHoursReportData = getFilteredManHoursReport(getManHoursProjectsDataQuery.data || [], jobTypesSet, salesPersonIds, divisionIds)

  const salesPersonNames = useMemo<string[]>(
    () => getManHoursProjectsDataQuery.data ? getUniqueSalesPersonNames(getManHoursProjectsDataQuery.data) : [],
    [getManHoursProjectsDataQuery.data]
  );

  const divisionOptions = useMemo<string[]>(
    () => getManHoursProjectsDataQuery.data ? getUniqueDivisionNames(getManHoursProjectsDataQuery.data) : [],
    [getManHoursProjectsDataQuery.data]
  );

  const onSaveFilter = () => {
    createFiltersMutation.mutate({
      request: {
        reportId: DatacenterReportTypes.ManHours,
        name: filterName,
        reportTypeName: getDatacenterReportTypeNames(DatacenterReportTypes.ManHours),
        filters: updateFilterDates(filters)
      },
      reportType: DatacenterReportTypes.ManHours
    });
    setOpen(false);
    setFilterName('');
  }

  return (<Row gutter={16}>
    {
      (filtersList.length > 0) && <Col span={4}>
        <ReportSavedFiltersList
          filtersList={filtersList}
          onFilterSelect={(filters) => setFilters(filters as ManHoursReportFilters)}
          onDeleteFilters={(selectedFiltersIds) => {
            deleteFilterModalConfirm.confirm({
              title: 'Do you want to delete the selected filters?',
              content: 'This action cannot be undone',
              onOk: () => {
                deleteFiltersMutation.mutate({ filtersIds: selectedFiltersIds, reportType: DatacenterReportTypes.ManHours })
              },
              okText: 'Delete',
              okButtonProps: {
                loading: deleteFiltersMutation.isLoading,
                danger: true
              }
            })
          }}
          renderFilterDetails={(filterItem) => <ManHoursReportFilterDetails filterItem={filterItem} />}
          reportType={DatacenterReportTypes.ManHours}
        />
      </Col>
    }

    <Col span={filtersList.length > 0 ? 20 : 24}>
      <VSpace>
        <ReportFilters
          dateRangeTitle='Work Area Completed Date'
          filters={filters}
          onFiltersChange={setFilters}
          isDataLoading={getManHoursProjectsDataQuery.isLoading}
          defaultFilters={defaultFilters}
          onSaveFilters={() => {
            setOpen(true)
          }}
          selectOptions={{
            salesPersonIds: {
              mode: 'multiple',
              options: salesPersonNames.map((name) => ({
                label: name,
                value: name,
              })),
              placeholder: 'Select Sales Persons',
              style: { width: 300 },
            },
            projectTypeIds: {
              mode: 'multiple',
              options: projectTypeOptions,
              placeholder: 'Select Project Types',
              style: { width: 300 },
            },
            divisionIds: {
              mode: 'multiple',
              options: divisionOptions.map((name) => ({
                label: name,
                value: name,
              })),
              placeholder: 'Select Divisions',
              style: { width: 300 },
            },
          }}
        />
        <ReportStatistics
          title={
            <span style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Estimated</span>
            </span>
          }
          statistics={getEstimatedManHoursStatistics(filteredManHoursReport)}
        />
        <ReportStatistics
          title={
            <span style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Actual</span>
            </span>
          }
          statistics={getActualManHoursStatistics(filteredManHoursReport)}
        />
        <ManHoursReportList report={filteredManHoursReport} dateRange={{ start: startDate, end: endDate }} />
      </VSpace>
    </Col>

    <SaveFilterModal
      inputValue={filterName}
      onCancel={() => {
        setFilterName('');
        setOpen(false);
      }}
      onInputChange={setFilterName}
      onOk={onSaveFilter}
      open={open}
      title='Save the Filter'
      okText='Save Filter'
      cancelText='Cancel'
    />
    {deleteFiltersModalContext}
  </Row>
  )
}

export default ManHoursReport 